import type { Risk, AlertType, AlertSeverity, AlertStatus } from "../types";

const typeDescriptions: Record<AlertType, string[]> = {
  leak: [
    "Potential Gas Leak Detected",
    "Minor Valve Seepage",
    "Pipeline Breach Alert",
    "Flange Leakage Detected",
    "Joint Seal Deterioration",
  ],
  pressure: [
    "Pressure Anomaly",
    "Abnormal Pressure Drop",
    "Pressure Surge Detected",
    "Low Pressure Warning",
    "Pressure Oscillation Alert",
  ],
  intrusion: [
    "Unauthorized Activity Detected",
    "Third Party Works Near Pipeline",
    "Perimeter Breach Detected",
    "Vehicle Incursion Alert",
    "Fence Line Tampering",
  ],
  equipment: [
    "Compressor Performance Degraded",
    "Valve Malfunction",
    "Sensor Communication Lost",
    "Power Supply Failure",
    "Control System Error",
  ],
  environmental: [
    "Ground Movement Detected",
    "Flooding Risk Alert",
    "Landslide Warning",
    "Extreme Temperature Alert",
    "Vegetation Encroachment",
  ],
};

const locations = [
  { name: "Manchester", coords: [-2.7378, 53.4808] as [number, number], region: "west" },
  { name: "Sheffield", coords: [-1.8983, 53.3811] as [number, number], region: "west" },
  { name: "London", coords: [-0.1257, 51.5074] as [number, number], region: "east" },
  { name: "Edinburgh", coords: [-3.1883, 55.9533] as [number, number], region: "scotland_north" },
  { name: "Durham", coords: [-1.5491, 54.5748] as [number, number], region: "scotland_north" },
  { name: "Lincoln", coords: [0.2747, 52.9547] as [number, number], region: "east" },
  { name: "Birmingham", coords: [-1.8908, 52.4862] as [number, number], region: "west" },
  { name: "Bristol", coords: [-2.5977, 51.4545] as [number, number], region: "west" },
  { name: "Leeds", coords: [-1.2407, 53.7997] as [number, number], region: "west" },
  { name: "Newcastle", coords: [-1.6178, 54.9783] as [number, number], region: "scotland_north" },
  { name: "Aberdeen", coords: [-2.0986, 57.1497] as [number, number], region: "scotland_north" },
  { name: "Glasgow", coords: [-4.2518, 55.8642] as [number, number], region: "scotland_north" },
  { name: "Carnforth", coords: [-2.77, 54.13] as [number, number], region: "west" },
  { name: "Peterborough", coords: [-0.24, 52.57] as [number, number], region: "east" },
  { name: "Norwich", coords: [1.2979, 52.6309] as [number, number], region: "east" },
];

const teams = ["Alpha Response", "Bravo Maintenance", "Charlie Operations", "Delta Emergency", "Echo Monitoring"];
const operators = ["John Smith", "Sarah Johnson", "Mike Brown", "Emily Davis", "David Wilson", "Lisa Anderson"];

function generateRisks(count: number): Risk[] {
  const risks: Risk[] = [];
  const types: AlertType[] = ["leak", "pressure", "intrusion", "equipment", "environmental"];
  const severities: AlertSeverity[] = ["critical", "high", "medium", "low"];
  const statuses: AlertStatus[] = ["active", "acknowledged", "investigating", "resolved", "closed"];

  // Add specific critical alerts first
  risks.push({
    id: "ALT-001",
    type: "leak",
    severity: "critical",
    status: "active",
    title: "Potential Gas Leak Detected",
    description: "Abnormal pressure drop detected in pipeline segment. Immediate inspection required. Sensors indicate potential breach in main transmission line.",
    coordinates: [-2.7378, 53.4808],
    location: "Manchester Area - Sector 7",
    timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
    pipelineSegment: "W-1",
    region: "West",
    assignedTeam: "Delta Emergency",
    notes: [
      {
        id: "N001",
        author: "System",
        content: "Alert automatically generated by pressure monitoring system",
        timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
      },
    ],
    actions: [
      {
        id: "A001",
        type: "acknowledge",
        actor: "System",
        description: "Alert generated and dispatched to emergency response team",
        timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
      },
    ],
  });

  risks.push({
    id: "ALT-002",
    type: "intrusion",
    severity: "critical",
    status: "investigating",
    title: "Third Party Works Near Pipeline",
    description: "Excavation activity detected within 50m of pipeline. Emergency response initiated. Ground penetrating radar confirms active digging.",
    coordinates: [-1.5491, 54.5748],
    location: "Durham - Construction Zone",
    timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
    acknowledgedAt: new Date(Date.now() - 4 * 60000).toISOString(),
    pipelineSegment: "SN-2",
    region: "Scotland North",
    assignedTo: "John Smith",
    assignedTeam: "Alpha Response",
    notes: [
      {
        id: "N002",
        author: "John Smith",
        content: "En route to location. ETA 10 minutes.",
        timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
      },
    ],
    actions: [
      {
        id: "A002",
        type: "acknowledge",
        actor: "John Smith",
        description: "Alert acknowledged",
        timestamp: new Date(Date.now() - 4 * 60000).toISOString(),
      },
      {
        id: "A003",
        type: "assign",
        actor: "Control Room",
        description: "Assigned to Alpha Response team",
        timestamp: new Date(Date.now() - 4 * 60000).toISOString(),
      },
    ],
  });

  // Generate more alerts
  for (let i = 3; i <= count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const severity = severities[Math.floor(Math.random() * (i < 10 ? 3 : 4))]; // More severe alerts early
    const location = locations[Math.floor(Math.random() * locations.length)];
    const titles = typeDescriptions[type];
    const title = titles[Math.floor(Math.random() * titles.length)];

    // Determine status based on severity and randomness
    let status: AlertStatus;
    const statusRand = Math.random();
    if (severity === "critical") {
      status = statusRand < 0.6 ? "active" : statusRand < 0.9 ? "investigating" : "acknowledged";
    } else if (severity === "high") {
      status = statusRand < 0.4 ? "active" : statusRand < 0.7 ? "investigating" : statusRand < 0.9 ? "acknowledged" : "resolved";
    } else {
      status = statuses[Math.floor(Math.random() * statuses.length)];
    }

    const hoursAgo = Math.floor(Math.random() * 72);
    const timestamp = new Date(Date.now() - hoursAgo * 3600000).toISOString();

    const risk: Risk = {
      id: `ALT-${String(i).padStart(3, "0")}`,
      type,
      severity,
      status,
      title,
      description: `${title}. Detected at ${location.name} area. ${getTypeDescription(type)}`,
      coordinates: location.coords,
      location: `${location.name} - Sector ${Math.floor(Math.random() * 12) + 1}`,
      timestamp,
      pipelineSegment: `${location.region.charAt(0).toUpperCase()}-${Math.floor(Math.random() * 4) + 1}`,
      region: location.region === "scotland_north" ? "Scotland North" : location.region === "west" ? "West" : "East",
    };

    // Add assignment for non-active alerts
    if (status !== "active") {
      risk.assignedTo = operators[Math.floor(Math.random() * operators.length)];
      risk.assignedTeam = teams[Math.floor(Math.random() * teams.length)];
      risk.acknowledgedAt = new Date(new Date(timestamp).getTime() + Math.random() * 1800000).toISOString();
    }

    // Add resolution time for resolved/closed
    if (status === "resolved" || status === "closed") {
      risk.resolvedAt = new Date(new Date(timestamp).getTime() + Math.random() * 7200000 + 1800000).toISOString();
    }

    risks.push(risk);
  }

  return risks.sort((a, b) => {
    // Sort by severity first, then by timestamp
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[a.severity] - severityOrder[b.severity];
    }
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

function getTypeDescription(type: AlertType): string {
  switch (type) {
    case "leak":
      return "Pressure sensors and acoustic monitors indicate possible pipeline integrity issue.";
    case "pressure":
      return "Real-time pressure monitoring systems have detected values outside normal operating parameters.";
    case "intrusion":
      return "Security systems have detected unauthorized presence in the protected zone.";
    case "equipment":
      return "Equipment diagnostics indicate performance degradation or malfunction.";
    case "environmental":
      return "Environmental monitoring sensors have detected conditions that may affect pipeline integrity.";
  }
}

// Generate 50 sample risks
export const risksData: Risk[] = generateRisks(50);

export const getRiskById = (id: string): Risk | undefined => {
  return risksData.find((risk) => risk.id === id);
};

export const getRiskStats = () => {
  const stats = {
    total: risksData.length,
    active: risksData.filter((r) => r.status === "active").length,
    investigating: risksData.filter((r) => r.status === "investigating").length,
    acknowledged: risksData.filter((r) => r.status === "acknowledged").length,
    resolved: risksData.filter((r) => r.status === "resolved").length,
    closed: risksData.filter((r) => r.status === "closed").length,
    bySeverity: {
      critical: risksData.filter((r) => r.severity === "critical").length,
      high: risksData.filter((r) => r.severity === "high").length,
      medium: risksData.filter((r) => r.severity === "medium").length,
      low: risksData.filter((r) => r.severity === "low").length,
    },
    byType: {
      leak: risksData.filter((r) => r.type === "leak").length,
      pressure: risksData.filter((r) => r.type === "pressure").length,
      intrusion: risksData.filter((r) => r.type === "intrusion").length,
      equipment: risksData.filter((r) => r.type === "equipment").length,
      environmental: risksData.filter((r) => r.type === "environmental").length,
    },
  };
  return stats;
};
